import { useCallback, useRef, useState } from 'react';
import { useResponsiveScale } from 'src/contexts/ResponsiveScaleContext';
import { useStore } from 'src/contexts/StoreContext';
import { IUserImageModel } from 'src/store/_models/userImage';
import { IUserImageAreaModel } from 'src/store/_models/userImageArea';
import { PreviewSvgModelInstance } from 'src/store/_models/previewSvg';

type TCoords = { x: number; y: number };
type TDragCursor =
  | (TCoords & { imageUrl: string; userImageAreaId: string; targetUserImageAreaId?: string })
  | undefined;

export function useDragCursorHandlers(preview: PreviewSvgModelInstance) {
  const {
    project: { setScrollingEnabled },
  } = useStore();
  const isDraggingRef = useRef(false);

  const [dragCursor, __setDragCursor] = useState<TDragCursor>();
  const setDragCursor = (value: TDragCursor) => {
    __setDragCursor(value);

    const isDragging = typeof value !== 'undefined';
    isDraggingRef.current = isDragging;
    setScrollingEnabled(!isDragging);
  };

  const { responsiveScale } = useResponsiveScale();

  const getUserImageAreaByCoords = useCallback(
    (coords: TCoords): IUserImageAreaModel | undefined => {
      const userImageArea = preview.userImageAreas.find(
        userImageArea =>
          coords.x > userImageArea.x * responsiveScale &&
          coords.x < userImageArea.x * responsiveScale + userImageArea.width * responsiveScale &&
          coords.y > userImageArea.y * responsiveScale &&
          coords.y < userImageArea.y * responsiveScale + userImageArea.height * responsiveScale,
      );
      return userImageArea;
    },
    [preview.userImageAreas, responsiveScale],
  );

  const dragCursorStart = (coords: TCoords) => {
    const userImageArea = getUserImageAreaByCoords(coords);
    // get user image for selected userImageArea
    const userImage = userImageArea?.userImage;

    if (!userImageArea || !userImage) {
      // no image grabbed
    } else {
      setDragCursor({ x: coords.x, y: coords.y, imageUrl: userImage.url, userImageAreaId: userImageArea.id });
    }
  };

  const dragCursorChange = (coords: TCoords) => {
    if (dragCursor) {
      const userImageArea = getUserImageAreaByCoords(coords);
      setDragCursor({ ...dragCursor, x: coords.x, y: coords.y, targetUserImageAreaId: userImageArea?.id });
    }
  };

  const dragCursorEnd = (coords: TCoords) => {
    if (!dragCursor) return; // no image grabbed
    const userImageArea = getUserImageAreaByCoords(coords);
    if (userImageArea && dragCursor.userImageAreaId !== userImageArea.id) {
      // switch images on store
      preview.swapImages(dragCursor.userImageAreaId, userImageArea.id);
    }
    dragCursorFinalize();
  };

  const dragCursorFinalize = () => {
    setDragCursor(undefined);
  };

  const isCursorStartingArea = (userImage: IUserImageModel) =>
    dragCursor && dragCursor.userImageAreaId === userImage.userImageAreaId;
  const isCursorEndingArea = (userImage: IUserImageModel) =>
    dragCursor && !isCursorStartingArea(userImage) && dragCursor.targetUserImageAreaId === userImage.userImageAreaId;
  const isCursorEnabled = (userImage: IUserImageModel) =>
    dragCursor && !isCursorEndingArea(userImage) && !isCursorStartingArea(userImage);

  return {
    dragCursor,
    isCursorStartingArea,
    isCursorEndingArea,
    isCursorEnabled,
    isCursorDragging: isDraggingRef.current,
    dragCursorStart,
    dragCursorChange,
    dragCursorEnd,
    dragCursorFinalize,
  };
}
